import React, { FC, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    TextField,
} from '@mui/material';
import { ApolloError, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Loader from 'components/shared/loader/loader.component';
import { emailPdfTemplate } from 'apollo/templates/invoices';
import { EmailPdf, Invoice, Type } from 'models/invoice';
import ChipInput from 'components/shared/chip-input/chip-input.component';
import { emailRegExp } from 'config/regex';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { InvoiceSettings } from 'models/client-directory';
import { Invoicing } from 'models/common';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';

type SendMailDialogComponentProps = {
    open: boolean;
    invoice: Invoice | null;
    onDialogClose: () => void;
    onSendMail: (error?: ApolloError, invoice?: Invoice) => void;
};
const SendMailDialogComponent: FC<SendMailDialogComponentProps> = ({
    open,
    invoice,
    onDialogClose,
    onSendMail,
}: SendMailDialogComponentProps) => {
    const { t: translate } = useTranslation(['common', 'invoices', 'orders']);
    const [isToEmailFieldValid, setIsToEmailFieldValid] = useState(true);
    const [isCcEmailFieldValid, setIsCcEmailFieldValid] = useState(true);
    const [isBccEmailFieldValid, setIsBccEmailFieldValid] = useState(true);
    const currentOrg = currentOrgVar();

    const labOverrideClinicForcedSetting = customerSettings.getCustomerSetting(
        currentOrg?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.ALLOW_LAB_INVOICE_OVERRIDE,
    );

    const [emailPdf, { loading }] = useMutation(emailPdfTemplate, {
        onCompleted: ({ emailPdf: invoice }) => {
            onSendMail(undefined, invoice as Invoice);
        },
        onError: (error: ApolloError) => {
            onSendMail(error, undefined);
        },
    });

    const validateEmail = (value: string) => {
        return emailRegExp.test(String(value).toLowerCase());
    };

    const validate = (values: EmailPdf) => {
        const errors: Record<string, string> = {};
        if (!values.to?.length) {
            errors.to = translate('invoices:VALIDATION:TO');
        }
        if (!values.body) {
            errors.body = translate('invoices:VALIDATION:BODY');
        }
        if (!values.replyAddress) {
            errors.replyAddress = translate('invoices:VALIDATION:REPLY_ADDRESS');
        }
        if (!validateEmail(values.replyAddress)) {
            errors.replyAddress = translate('common:VALIDATION:REPLY_ADDRESS');
        }
        return errors;
    };

    const createSubject = () => {
        const settings = invoice?.invoiceSettings?.emailSettings;
        if (settings && invoice) {
            return invoice.type === Type.DEBIT ? settings.subject : settings.creditSubject;
        }
        return '';
    };

    const createBody = (): string => {
        const settings = invoice?.invoiceSettings?.emailSettings;
        if (settings && invoice) {
            return invoice.type === Type.DEBIT ? (settings.body as string) : (settings.creditBody as string);
        }
        return '';
    };

    const getRecipientEmail = (
        invoiceSettings: InvoiceSettings,
        invoicing: Invoicing,
        labOverrideClinicForcedSetting: boolean,
    ) => {
        if (labOverrideClinicForcedSetting) {
            return invoiceSettings?.emailSettings?.to || [];
        }
        if (invoicing?.communication?.email) {
            return [invoicing?.communication?.email];
        }
        return invoiceSettings?.emailSettings?.to || [];
    };

    const formik = useFormik({
        initialValues: {
            invoiceId: invoice?._id as string,
            replyAddress:
                invoice?.invoiceSettings?.emailSettings?.replyAddress ||
                currentOrg?.invoicing?.communication?.email ||
                '',
            to: getRecipientEmail(
                invoice?.invoiceSettings || ({} as InvoiceSettings),
                invoice?.mainClinic?.invoicing || ({} as Invoicing),
                labOverrideClinicForcedSetting,
            ),
            cc: invoice?.invoiceSettings?.emailSettings?.cc || [],
            bcc: invoice?.invoiceSettings?.emailSettings?.bcc || [],
            subject: createSubject(),
            body: createBody(),
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: async (values: EmailPdf) => {
            const { invoiceId, ...emailSettings } = values;
            const variables = { invoiceId, emailSettings };
            await emailPdf({ variables });
            onDialogClose();
        },
    });

    const handleFieldChange = (field: string, value: string[]) => {
        formik.setFieldValue(field, value);
    };

    const handleToError = (err: Error | null) => {
        setIsToEmailFieldValid(!err);
    };

    const handleCcError = (err: Error | null) => {
        setIsCcEmailFieldValid(!err);
    };

    const handleBccError = (err: Error | null) => {
        setIsBccEmailFieldValid(!err);
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onDialogClose}>
            <DialogTitle>
                {invoice?.type === Type.DEBIT ? (
                    <span>{translate('invoices:SEND_INVOICE')}</span>
                ) : (
                    <span>{translate('invoices:SEND_CREDIT_INVOICE')}</span>
                )}
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    required
                                    id="reply-address"
                                    margin="dense"
                                    label={translate('invoices:REPLY_ADDRESS')}
                                    helperText={formik.touched.replyAddress && formik.errors.replyAddress}
                                    error={formik.touched.replyAddress && Boolean(formik.errors.replyAddress)}
                                    {...formik.getFieldProps('replyAddress')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <ChipInput
                                    label={translate('invoices:TO')}
                                    required
                                    errorMessage={translate('common:VALIDATION:TO')}
                                    createChipOnBlur
                                    chipValidator={validateEmail}
                                    onError={handleToError}
                                    placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                    options={[]}
                                    defaultValue={formik.values.to}
                                    onValueChange={(value: string[]) => handleFieldChange('to', value)}
                                    onDelete={(value: string[]) => handleFieldChange('to', value)}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <ChipInput
                                    label={translate('invoices:CC')}
                                    placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                    options={[]}
                                    createChipOnBlur
                                    chipValidator={validateEmail}
                                    onError={handleCcError}
                                    defaultValue={formik.values.cc}
                                    onValueChange={(value: string[]) => handleFieldChange('cc', value)}
                                    onDelete={(value: string[]) => handleFieldChange('cc', value)}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <ChipInput
                                    label={translate('invoices:BCC')}
                                    placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                    options={[]}
                                    createChipOnBlur
                                    chipValidator={validateEmail}
                                    onError={handleBccError}
                                    defaultValue={formik.values.bcc}
                                    onValueChange={(value: string[]) => handleFieldChange('bcc', value)}
                                    onDelete={(value: string[]) => handleFieldChange('bcc', value)}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="subject"
                                    label={translate('invoices:SUBJECT')}
                                    {...formik.getFieldProps('subject')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {translate('orders:INVOICE')} {invoice?.invoiceNumber}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="body"
                                    placeholder={translate('invoices:EMAIL_BODY')}
                                    required
                                    minRows={6}
                                    multiline
                                    helperText={formik.touched.body && formik.errors.body}
                                    error={formik.touched.body && Boolean(formik.errors.body)}
                                    {...formik.getFieldProps('body')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                {loading ? (
                    <Loader />
                ) : (
                    <DialogActions>
                        <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>
                        <Button
                            disabled={
                                !formik.isValid || !isCcEmailFieldValid || !isBccEmailFieldValid || !isToEmailFieldValid
                            }
                            variant="contained"
                            type="submit"
                        >
                            {translate('invoices:SEND')}
                        </Button>
                    </DialogActions>
                )}
            </form>
        </Dialog>
    );
};

export default SendMailDialogComponent;
